import {SortOrder} from '@domain/hroc-checklists'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useMediaQuery, useTheme} from '@mui/material'
import {
  RefererName,
  type ChecklistTemplate,
  HrocChecklistsPathSegments
} from '@widgets/hroc-checklists'
import {generatePath, useNavigate} from 'react-router'

import {
  ChecklistTemplateContainer,
  ChecklistTemplatesContainer,
  ChecklistTemplatesListContainer
} from '../containers'
import {useChecklistTemplatesPageSearchParameters, SearchParamsMode} from '../hooks'
import {ChecklistsTemplatesDesktopLayout, ChecklistsTemplatesMobileLayout} from '../layouts'

type Props = {
  plantId: string
  titleSlot?: React.ReactNode
  aboveTitleSlot?: React.ReactNode
}

const DEFAULT_PAGE = 0
const DEFAULT_ROWS_PER_PAGE = 10

export const ChecklistsTemplatesPage = ({plantId, titleSlot, aboveTitleSlot}: Props) => {
  const [pageSearch, setPageSearch] = useChecklistTemplatesPageSearchParameters()
  const timezone = useUpmPlantTimezone(plantId)
  const navigate = useNavigate()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const isTemplateDetailsMode = pageSearch.mode === SearchParamsMode.TEMPLATE_DETAILS
  const selectedTemplateId = isTemplateDetailsMode ? pageSearch.templateId : undefined
  const isTemplateDetailsDisplayed = isTemplateDetailsMode && selectedTemplateId

  const handleTemplateClick = (template: ChecklistTemplate) => {
    setPageSearch({mode: SearchParamsMode.TEMPLATE_DETAILS, templateId: template.id})
  }

  const handleSortChecklistsChange = (_columnKey: string, sortOrder: SortOrder) => {
    if (isTemplateDetailsMode) {
      setPageSearch({
        ...pageSearch,
        sortOrder
      })
    }
  }

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    if (isTemplateDetailsMode) {
      setPageSearch({
        ...pageSearch,
        page,
        rowsPerPage
      })
    }
  }

  const handleChecklistClick = (checklistId: string) => {
    const targetPath = generatePath(HrocChecklistsPathSegments.Checklist, {
      checklistId
    })
    const searchParams = new URLSearchParams({
      refererName: RefererName.ChecklistTemplates
    })
    const path = `${targetPath}?${searchParams.toString()}`

    void navigate(path)
  }

  const templateDetailsComponent = isTemplateDetailsDisplayed ? (
    <ChecklistTemplateContainer
      plantId={plantId}
      templateId={selectedTemplateId}
      onClose={() => setPageSearch({mode: SearchParamsMode.TABLE_ONLY})}
      page={pageSearch.page ?? DEFAULT_PAGE}
      rowsPerPage={pageSearch.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE}
      onPaginationChange={handlePaginationChange}
      sortOrder={pageSearch.sortOrder ?? SortOrder.DESCENDING}
      onSortChange={handleSortChecklistsChange}
      onChecklistClick={handleChecklistClick}
      timezone={timezone}
    />
  ) : null

  if (isMobile) {
    return (
      <ChecklistsTemplatesMobileLayout
        titleSlot={titleSlot}
        aboveTitleSlot={aboveTitleSlot}
        templatesListSlot={
          <ChecklistTemplatesListContainer
            plantId={plantId}
            selectedTemplateId={selectedTemplateId}
            onTemplateClick={handleTemplateClick}
            timezone={timezone}
          />
        }
        templateDetailsSlot={templateDetailsComponent}
      />
    )
  }

  return (
    <ChecklistsTemplatesDesktopLayout
      titleSlot={titleSlot}
      aboveTitleSlot={aboveTitleSlot}
      templatesTableSlot={
        <ChecklistTemplatesContainer
          plantId={plantId}
          selectedTemplateId={selectedTemplateId}
          onTemplateClick={handleTemplateClick}
          timezone={timezone}
        />
      }
      templateDetailsSlot={templateDetailsComponent}
    />
  )
}
